import { msg } from "@lingui/core/macro";
import { createEffect, createSignal, For, Show } from "solid-js";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import { Mail, Pencil, Plus, Trash2 } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { useAdminCapabilities } from "../use-admin-capabilities";
import { Badge, Skeleton } from "@fleet/ui";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@time-tracker/shared";
import { useI18n } from "@/providers/i18n-provider";
import { EmailTemplateEditor } from "./email-template-editor";

type EmailTemplate = components["schemas"]["EmailTemplate"];

/**
 * Email template CRUD (campaign #11). Lists templates (system +
 * custom), and allows creating/editing/deleting them. System templates cannot be deleted.
 */
export function EmailTemplateManager() {
  const { t } = useI18n();
  // `content.write` (mirror of `Capability::ManageContent`): the backend 403s
  // these mutations by method in middleware, so the UI does not offer them.
  // Reading the panel remains available to `read_only`.
  const { can } = useAdminCapabilities();
  const canWrite = () => can("content.write");
  const [editing, setEditing] = createSignal<EmailTemplate | null>(null);
  const [creating, setCreating] = createSignal(false);

  // Deep-link to the template editor: `?dialog=new` / `?dialog=edit&id=<tplId>`.
  // We write it to the URL on open/close and an effect hydrates from the URL (pasted
  // links, back/forward). To edit, we resolve the row from the listing.
  const urlSearch = useSearch({ strict: false });
  const navigate = useNavigate();

  const urlDialog = (): string | undefined => {
    const v = (urlSearch() as { dialog?: unknown }).dialog;
    return typeof v === "string" ? v : undefined;
  };
  const urlId = (): string | undefined => {
    const v = (urlSearch() as { id?: unknown }).id;
    return typeof v === "string" && v !== "" ? v : undefined;
  };

  const setEditorUrl = (params: { dialog?: "new" | "edit"; id?: string }) => {
    void navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        dialog: params.dialog,
        id: params.id,
      }),
      replace: true,
    });
  };

  const templatesQuery = createApiQuery<EmailTemplate[], components["schemas"]["ErrorModel"]>(
    () => ["admin", "email", "templates"],
    async () => api.GET("/api/admin/email/templates"),
    () => ({ staleTime: 30_000 }),
  );

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    setEditorUrl({ dialog: "new" });
  };

  const openEdit = (tpl: EmailTemplate) => {
    setCreating(false);
    setEditing(tpl);
    setEditorUrl({ dialog: "edit", id: tpl.id });
  };

  const closeEditor = () => {
    setEditing(null);
    setCreating(false);
    setEditorUrl({});
  };

  // URL → state: hydrate the editor indicated by the URL.
  createEffect(() => {
    const dialog = urlDialog();
    const id = urlId();
    if (dialog === "new") {
      if (!creating()) {
        setEditing(null);
        setCreating(true);
      }
      return;
    }
    if (dialog === "edit" && id) {
      const tpl = (templatesQuery.data ?? []).find((row) => row.id === id);
      if (tpl && editing()?.id !== id) {
        setCreating(false);
        setEditing(tpl);
      }
      return;
    }
    if (creating()) {
      setCreating(false);
    }
    if (editing()) {
      setEditing(null);
    }
  });

  const deleteMutation = createApiMutation(async (vars: { id: string }) =>
    api.POST("/api/admin/email/templates/delete", { body: vars }),
  );

  const handleDelete = async (tpl: EmailTemplate) => {
    if (tpl.isSystem) {
      return;
    }
    try {
      await deleteMutation.mutateAsync({ id: tpl.id });
      await templatesQuery.refetch();
    } catch (err) {
      console.error("Error deleting email template:", err);
    }
  };

  const onSaved = async () => {
    setEditing(null);
    setCreating(false);
    setEditorUrl({});
    await templatesQuery.refetch();
  };

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Mail class="h-8 w-8 text-primary" />
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Email Templates`)}</h2>
        </div>
        <Show when={canWrite()}>
          <Button class="gap-2" onClick={openCreate}>
            <Plus class="h-4 w-4" />
            {t(msg`New Template`)}
          </Button>
        </Show>
      </div>

      <Show when={creating() || editing()}>
        <EmailTemplateEditor template={editing()} onSaved={onSaved} onCancel={closeEditor} />
      </Show>

      {/* P23 / CC-60 - I3: <<no se pudo cargar>> NO es <<no hay nada>>. Con el API
          caido este panel pintaba su estado VACIO como si el dato fuera real. El
          bloque de contenido se ENVUELVE en lugar de encadenar `&& !templatesQuery.isError`
          en el guard existente: en error `isLoading` es `false`, asi que el guard
          pintaria su esqueleto Y la caja de error a la vez, para siempre. */}
      <Show when={templatesQuery.isError}>
        <QueryErrorState
          testId="admin-email-templates-error"
          compact
          onRetry={() => void templatesQuery.refetch()}
        />
      </Show>
      <Show when={!templatesQuery.isError}>
        <Show
          when={!templatesQuery.isLoading}
          fallback={
            <div class="space-y-3">
              <For each={Array.from({ length: 4 }, () => 0)}>
                {() => <Skeleton class="h-20 w-full rounded-xl" />}
              </For>
            </div>
          }
        >
          <div class="space-y-3">
            <For each={templatesQuery.data ?? []}>
              {(tpl) => (
                <div class="flex items-center justify-between rounded-xl border border-border bg-card p-4">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="truncate font-semibold">{tpl.name}</span>
                      <Badge variant="secondary">{tpl.category}</Badge>
                      <Show when={tpl.isSystem}>
                        <Badge>{t(msg`System`)}</Badge>
                      </Show>
                      <Show when={!tpl.isActive}>
                        <Badge variant="outline">{t(msg`Inactive`)}</Badge>
                      </Show>
                    </div>
                    <p class="mt-1 truncate text-sm text-muted-foreground">{tpl.subject}</p>
                    <p class="mt-0.5 text-xs text-muted-foreground">
                      {(() => {
                        const { key } = tpl;
                        const version = tpl.currentVersion;
                        return t(msg`Key: ${key} · v${version}`);
                      })()}
                    </p>
                  </div>
                  <div class="flex shrink-0 items-center gap-2">
                    <Button variant="ghost" size="sm" class="gap-1" onClick={() => openEdit(tpl)}>
                      <Pencil class="h-4 w-4" />
                      {t(msg`Edit`)}
                    </Button>
                    <Show when={!tpl.isSystem && canWrite()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="gap-1 text-destructive"
                        disabled={deleteMutation.isPending}
                        onClick={() => handleDelete(tpl)}
                      >
                        <Trash2 class="h-4 w-4" />
                        {t(msg`Delete`)}
                      </Button>
                    </Show>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
}
